import type { PostHog, Properties } from "posthog-js";

import { restoreCampaignParams } from "@/components/posthog/campaign-params";
import type { CampaignParams } from "@/components/posthog/campaign-params";

const PERFORMANCE_COOKIE_CATEGORY_ID = "C0002";

/**
 * OneTrust reports its active categories as a comma-wrapped string
 * (`",C0001,C0002,"`) on `window.OnetrustActiveGroups`, and as either that
 * string or an array of IDs on the `OneTrustGroupsUpdated` event detail.
 */
export function isPerformanceConsentGiven(
  activeGroups?: string | string[] | null
): boolean {
  return parseConsentGroups(activeGroups).some(
    (group) => group === PERFORMANCE_COOKIE_CATEGORY_ID
  );
}

/**
 * Whether OneTrust has actually published a consent decision.
 *
 * `window.OnetrustActiveGroups` is undefined (or empty) between the SDK bundle
 * loading and it applying the visitor's stored preferences. Absent categories in
 * that window mean "not known yet", not "denied" — strictly-necessary (C0001) is
 * always present once a real decision has been applied. Treating the empty
 * global as a denial would downgrade consented sessions to cookieless.
 */
export function hasPublishedConsentDecision(
  activeGroups?: string | string[] | null
): boolean {
  return parseConsentGroups(activeGroups).length > 0;
}

function parseConsentGroups(activeGroups?: string | string[] | null): string[] {
  const groups = Array.isArray(activeGroups)
    ? activeGroups
    : (activeGroups ?? "").split(",");
  return groups
    .map((group) => group.trim())
    .filter((group) => group.length > 0);
}

/**
 * Moves PostHog to `granted`, keeping campaign attribution intact across the
 * identity reset that the transition performs.
 *
 * Returns `true` when consent actually changed, which means the caller owns
 * capturing the single `$pageview` for the new identity. Returns `false` for a
 * repeat notification: `OneTrustGroupsUpdated` also fires on page load with
 * consent unchanged, and opting in again would reset identity and duplicate the
 * pageview.
 */
export function applyPerformanceConsent(
  posthog: PostHog,
  granted: boolean,
  {
    campaignParams,
    superProperties,
  }: { campaignParams: CampaignParams; superProperties?: Properties }
): boolean {
  // Only a no-op when the SDK already sits in the target state. A "pending"
  // status still needs applying — that is what starts cookieless capture.
  if (
    posthog.get_explicit_consent_status() === (granted ? "granted" : "denied")
  ) {
    return false;
  }

  // Both SDK calls fire their own initial $pageview when capture_pageview is
  // truthy, and it would land before the campaign params are restored below.
  // That capture is also one-shot per page load, so whether it fires at all
  // depends on which manual captures happened first. Suppress it and let the
  // caller capture one pageview deterministically instead.
  const { capture_pageview } = posthog.config;
  posthog.set_config({ capture_pageview: false });
  try {
    if (granted) {
      // Skip the SDK's own $opt_in so it can be captured below, after the params
      // are restored: it is the first event of the new session, and PostHog
      // derives the session's entry UTMs from the earliest event in it.
      posthog.opt_in_capturing({ captureEventName: false });
    } else {
      posthog.opt_out_capturing();
    }
  } finally {
    posthog.set_config({ capture_pageview });
  }

  // Both directions reset() out of or into cookieless mode, and reset() clears
  // persistence — which drops registered super properties as well as the
  // campaign params. Re-register both before anything is captured, so the
  // $opt_in below is tagged consistently with every later event. Registering
  // them earlier would not help: the reset would wipe them again.
  if (superProperties) {
    posthog.register_once(superProperties);
  }
  restoreCampaignParams(posthog, campaignParams);

  if (granted) {
    posthog.capture("$opt_in", undefined, { send_instantly: true });
  }
  return true;
}
